/** @typedef {import("../../api/types").PublicOrder} PublicOrder */
import React, {useEffect, useState} from "react";
import axios from "axios";
import {useParams} from "react-router-dom";
import {Alert, Button, Card, CardBody, CardHeader, Container, ListGroup, ListGroupItem, Spinner} from "reactstrap";
import {useProviderTheme} from "../../hooks/useProviderTheme";
import ConfirmModal from "../../shared/components/ConfirmModal";
import EmailLink from "../../shared/components/EmailLink";
import PhoneLink from "../../shared/components/PhoneLink";
import {formatDate} from "../../shared/utils/format";
import PixQrModal from "../../shared/components/PixQrModal";
import {copyPromisedText} from "../../shared/utils/clipboard";
import OrderStatusBadge from "../orders/components/OrderStatusBadge";
import PixCodeModal from "../orders/components/PixCodeModal";
import {DeviceSummary, DiscountSummary, ItemSummary, TotalsCard} from "../orders/components/OrderSummaries";
import IconLabel from "../../shared/components/IconLabel";
import {
    BoxIcon,
    CheckIcon,
    DeviceIcon,
    ListIcon,
    PercentIcon,
    PixIcon,
    QrIcon,
    ShopIcon,
    UserIcon,
    XCircleIcon,
} from "../../shared/components/icons";

const ACTIONS = {
    confirm: {
        label: "Confirmar ordem",
        title: "Tem certeza?",
        message: "Ao confirmar, você aceita os serviços e o total desta ordem.",
        confirmLabel: "Sim, confirmar",
        icon: CheckIcon,
        color: "primary",
        done: "Ordem confirmada. Obrigado!",
    },
    cancel: {
        label: "Cancelar ordem",
        title: "Tem certeza?",
        message: "Esta ação não pode ser desfeita.",
        confirmLabel: "Sim, cancelar",
        icon: XCircleIcon,
        color: "danger",
        outline: true,
        done: "Ordem cancelada.",
    },
};

// Why the Pix code couldn't be made, by the API's `code`.
const PIX_MESSAGES = {
    pix_key_missing: "O prestador ainda não cadastrou uma chave Pix. Entre em contato para combinar o pagamento.",
    pix_currency_not_supported: "O Pix só funciona com ordens em reais (R$). Entre em contato com o prestador.",
    pix_not_available: "Esta ordem não está mais aguardando pagamento.",
};

// fields_content is a JSON-encoded object (string) per the API schema.
const parseFields = (content) => {
    try {
        const parsed = JSON.parse(content || "{}");
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
        return {};
    }
};

const Notice = ({title, children}) => (
    <Container className="mt-5 text-center" style={{maxWidth: 480}}>
        <h3>{title}</h3>
        <p className="text-muted">{children}</p>
    </Container>
);

/**
 * An order as its customer sees it, at /quotes/<token>, with no login. While
 * the order is Sent they can confirm or cancel it; once they do, the page
 * says what they chose. Once the price is final (confirmed, or done and not
 * yet paid) they can copy the Pix code to pay it. The link stays a read-only
 * view of the order unless it is canceled.
 */
const Quote = () => {
    const {token} = useParams();
    const url = `/os/api/public/orders/${encodeURIComponent(token)}/`;
    const [order, setOrder] = useState(/** @type {PublicOrder|null} */ (null));
    const [status, setStatus] = useState(/** @type {"loading"|"ready"|"missing"|"expired"|"error"} */ ("loading"));
    const [pending, setPending] = useState(/** @type {keyof typeof ACTIONS|null} */ (null));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(/** @type {string|null} */ (null));
    // What the customer just did, shown in place of the buttons.
    const [answered, setAnswered] = useState(/** @type {keyof typeof ACTIONS|null} */ (null));
    const [pixBusy, setPixBusy] = useState(false);
    const [pixNotice, setPixNotice] = useState(/** @type {{color: string, text: string}|null} */ (null));
    // The code itself, for when the browser wouldn't let us copy it.
    const [pixCode, setPixCode] = useState(/** @type {string|null} */ (null));
    const [qrCode, setQrCode] = useState(/** @type {string|null} */ (null));

    useEffect(() => {
        const controller = new AbortController();
        setStatus("loading");
        setOrder(null);

        axios
            .get(url, {signal: controller.signal})
            .then(({data}) => {
                setOrder(data);
                setStatus("ready");
            })
            .catch((err) => {
                if (axios.isCancel(err)) return;
                const code = err.response?.status;
                setStatus(code === 404 ? "missing" : code === 410 ? "expired" : "error");
            });

        return () => controller.abort();
    }, [url]);

    // The order wears its provider's color.
    useProviderTheme(order ? order.theme : undefined);

    // The tab shows who the order is from.
    useEffect(() => {
        if (!order) return undefined;
        const previous = document.title;
        document.title = `Ordem nº ${order.number} · ${order.provider}`;
        return () => {
            document.title = previous;
        };
    }, [order]);

    const run = async () => {
        if (!pending) return;
        setBusy(true);
        setError(null);
        try {
            const {data} = await axios.post(`${url}${pending}/`);
            setOrder(data);
            setAnswered(pending);
        } catch (err) {
            const code = err.response?.status;
            if (code === 410) {
                setStatus("expired");
            } else if (code === 404) {
                setStatus("missing");
            } else if (code === 409) {
                setError("Esta ordem não pode mais ser respondida.");
            } else {
                setError("Não foi possível concluir a ação. Tente novamente.");
            }
        } finally {
            setBusy(false);
            setPending(null);
        }
    };

    // Why the Pix code couldn't be made, as a notice (or the page's state, when the link died).
    const explainPix = (err) => {
        const code = err.response?.status;
        if (code === 410) {
            setStatus("expired");
        } else if (code === 404) {
            setStatus("missing");
        } else {
            setPixNotice({
                color: "danger",
                text: PIX_MESSAGES[err.response?.data?.code] ?? "Não foi possível gerar o código Pix. Tente novamente.",
            });
        }
    };

    const copyPix = async () => {
        setPixBusy(true);
        setPixNotice(null);
        const payload = axios.get(`${url}pix/`).then(({data}) => /** @type {string} */ (data.payload));
        payload.catch(() => {}); // handled below; avoids an "unhandled" warning while copying
        try {
            await copyPromisedText(payload);
            setPixNotice({
                color: "success",
                text: "Código Pix copiado. No app do seu banco, escolha Pix Copia e Cola e cole o código.",
            });
        } catch {
            try {
                setPixCode(await payload);
            } catch (err) {
                explainPix(err);
            }
        } finally {
            setPixBusy(false);
        }
    };

    // For paying from another device: read the same code with the phone's camera.
    const showQr = async () => {
        setPixBusy(true);
        setPixNotice(null);
        try {
            const {data} = await axios.get(`${url}pix/`);
            setQrCode(data.payload);
        } catch (err) {
            explainPix(err);
        } finally {
            setPixBusy(false);
        }
    };

    if (status === "loading") {
        return (
            <div className="text-center mt-5">
                <Spinner />
            </div>
        );
    }
    if (status === "missing") {
        return (
            <Notice title="Ordem não encontrada">
                O endereço pode estar incorreto ou a ordem não está mais disponível.
            </Notice>
        );
    }
    if (status === "expired") {
        return <Notice title="Link expirado">Este link não vale mais. Entre em contato com o prestador.</Notice>;
    }
    if (status !== "ready" || !order) {
        return (
            <Container className="mt-5 text-center" style={{maxWidth: 480}}>
                <Alert color="danger">Não foi possível carregar a ordem. Tente novamente.</Alert>
            </Container>
        );
    }

    const {currency, order_items: items, order_devices: devices} = order;
    const products = order.order_products ?? [];
    const discountAmount = Number(order.formatted_absolute_discount) || 0;
    const total = Number(order.total_value);
    const actions = /** @type {(keyof typeof ACTIONS)[]} */ (order.available_actions.filter((name) => name in ACTIONS));
    const canPix = order.available_actions.includes("pix");

    return (
        <Container className="mt-4 mb-5" style={{maxWidth: 860}}>
            <div className="mb-3">
                <div className="text-muted">{order.provider}</div>
                <div className="d-flex align-items-center">
                    <h3 className="mb-0 me-3">Ordem de serviço nº {order.number}</h3>
                    <OrderStatusBadge status={order.status} />
                </div>
                <div className="text-muted small mt-1">
                    Criada em {formatDate(order.created_at)}
                    {order.expiration && ` · Validade: ${formatDate(order.expiration)}`}
                </div>
            </div>

            {order.status === "Open" && (
                <Alert color="info" className="mb-3">
                    Esta ordem ainda está sendo criada. Em breve você poderá confirmá-la ou cancelá-la por aqui.
                </Alert>
            )}

            {answered && (
                <Alert color={answered === "confirm" ? "success" : "secondary"} className="mb-3">
                    {ACTIONS[answered].done}
                </Alert>
            )}

            <div className="row g-3 mb-3">
                <div className="col-12 col-md-6">
                    <Card className="h-100">
                        <CardHeader>
                            <IconLabel icon={<ShopIcon />}>Prestador</IconLabel>
                        </CardHeader>
                        <CardBody>
                            <div className="fw-semibold">{order.provider}</div>
                            {order.phone && (
                                <div>
                                    <PhoneLink phone={order.phone} />
                                </div>
                            )}
                            {order.provider_email && (
                                <div>
                                    <EmailLink email={order.provider_email} />
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </div>
                <div className="col-12 col-md-6">
                    <Card className="h-100">
                        <CardHeader>
                            <IconLabel icon={<UserIcon />}>Cliente</IconLabel>
                        </CardHeader>
                        <CardBody>
                            <div className="fw-semibold">{order.client}</div>
                            {order.observations && (
                                <div className="mt-2" style={{whiteSpace: "pre-wrap"}}>
                                    {order.observations}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </div>
            </div>

            {devices.length > 0 && (
                <Card className="mb-3">
                    <CardHeader>
                        <IconLabel icon={<DeviceIcon />}>Equipamentos do cliente</IconLabel>
                    </CardHeader>
                    <CardBody>
                        <ListGroup>
                            {devices.map((device, index) => (
                                <ListGroupItem className="d-flex align-items-start gap-2" key={index}>
                                    <DeviceSummary
                                        typeName={device.device_type ?? undefined}
                                        description={device.description}
                                        fields={parseFields(device.fields_content)}
                                    />
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    </CardBody>
                </Card>
            )}

            <Card className="mb-3">
                <CardHeader>
                    <IconLabel icon={<ListIcon />}>Serviços</IconLabel>
                </CardHeader>
                <CardBody>
                    {items.length ? (
                        <ListGroup>
                            {items.map((item, index) => (
                                <ListGroupItem className="d-flex align-items-start gap-2" key={index}>
                                    <ItemSummary
                                        name={item.name}
                                        quantity={item.quantity}
                                        unit={Number(item.base_value)}
                                        total={Number(item.value)}
                                        currency={currency}
                                    />
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    ) : (
                        <span className="text-muted">Nenhum serviço.</span>
                    )}
                </CardBody>
            </Card>

            {products.length > 0 && (
                <Card className="mb-3">
                    <CardHeader>
                        <IconLabel icon={<BoxIcon />}>Produtos</IconLabel>
                    </CardHeader>
                    <CardBody>
                        <ListGroup>
                            {products.map((line, index) => (
                                <ListGroupItem className="d-flex align-items-start gap-2" key={index}>
                                    <ItemSummary
                                        name={line.name}
                                        quantity={line.quantity}
                                        unit={Number(line.base_value)}
                                        total={Number(line.value)}
                                        currency={currency}
                                    />
                                </ListGroupItem>
                            ))}
                        </ListGroup>
                    </CardBody>
                </Card>
            )}

            {order.discount_type && Number(order.discount_value) > 0 && (
                <Card className="mb-3">
                    <CardHeader>
                        <IconLabel icon={<PercentIcon />}>Desconto</IconLabel>
                    </CardHeader>
                    <CardBody>
                        <ListGroup>
                            <ListGroupItem className="d-flex align-items-start gap-2">
                                <DiscountSummary
                                    type={order.discount_type}
                                    value={Number(order.discount_value)}
                                    description={order.discount_description ?? undefined}
                                    amount={discountAmount}
                                    currency={currency}
                                />
                            </ListGroupItem>
                        </ListGroup>
                    </CardBody>
                </Card>
            )}

            <TotalsCard
                subtotal={total + discountAmount}
                discount={discountAmount}
                total={total}
                currency={currency}
                totalLabel="Total"
            />

            {error && (
                <Alert color="danger" className="mb-3">
                    {error}
                </Alert>
            )}
            {pixNotice && (
                <Alert color={pixNotice.color} className="mb-3">
                    {pixNotice.text}
                </Alert>
            )}
            {(actions.length > 0 || canPix) && (
                <div className="d-flex flex-wrap justify-content-center gap-2">
                    {canPix && (
                        <>
                            <Button color="success" disabled={pixBusy} onClick={copyPix}>
                                <IconLabel icon={<PixIcon size={18} />}>Copiar código Pix</IconLabel>
                            </Button>
                            <Button color="success" outline disabled={pixBusy} onClick={showQr}>
                                <IconLabel icon={<QrIcon />}>Mostrar QR code</IconLabel>
                            </Button>
                        </>
                    )}
                    {actions.map((name) => (
                        <Button
                            key={name}
                            color={ACTIONS[name].color}
                            outline={ACTIONS[name].outline}
                            onClick={() => setPending(name)}
                        >
                            <IconLabel icon={React.createElement(ACTIONS[name].icon)}>{ACTIONS[name].label}</IconLabel>
                        </Button>
                    ))}
                </div>
            )}
            {pixCode && (
                <PixCodeModal
                    payload={pixCode}
                    message="Não foi possível copiar automaticamente. Copie o código abaixo e cole no app do seu banco (Pix Copia e Cola)."
                    onClose={() => setPixCode(null)}
                />
            )}
            {qrCode && (
                <PixQrModal
                    payload={qrCode}
                    onClose={() => setQrCode(null)}
                    hint="Abra o app do seu banco, escolha pagar com Pix e leia o QR code."
                />
            )}
            {pending && (
                <ConfirmModal
                    isOpen
                    title={ACTIONS[pending].title}
                    message={ACTIONS[pending].message}
                    confirmLabel={ACTIONS[pending].confirmLabel}
                    confirmIcon={ACTIONS[pending].icon}
                    color={ACTIONS[pending].color}
                    busy={busy}
                    onConfirm={run}
                    onCancel={() => setPending(null)}
                />
            )}
        </Container>
    );
};

export default Quote;
